import useGeneralSettings from '@hooks/useGeneralSettings';
import { useCurrency } from '@providers/CurrencyProvider';
import type { FilterOptionListType, FilterOptionType } from '@providers/FilterProvider';
import {
  FILTER_OPTION_DISPLAY_TYPE,
  FILTER_OPTION_TYPE,
  useFilterSettings,
  useFilterState,
  useFilterTreeState,
} from '@providers/FilterProvider';
import { pick } from 'lodash-es';

import FilterOptionBox from '@/features/filter-product-list/filter/FilterOptionBox';
import FilterOptionList from '@/features/filter-product-list/filter/FilterOptionList';
import FilterOptionMultiLevelCollections from '@/features/filter-product-list/filter/FilterOptionMultiLevelCollections';
import FilterOptionPercentSale from '@/features/filter-product-list/filter/FilterOptionPercentSale';
import FilterOptionPriceList from '@/features/filter-product-list/filter/FilterOptionPriceList';
import FilterOptionRangeSlider from '@/features/filter-product-list/filter/FilterOptionRangeSlider';
import FilterOptionRating from '@/features/filter-product-list/filter/FilterOptionRating';
import FilterOptionSwatch from '@/features/filter-product-list/filter/FilterOptionSwatch';

import { useFilterOptionCollectionItem } from '../connectors/useFilterOptionCollectionItem';

type FilterOptionByTypeProps = {
  option: FilterOptionType;
  currentModifierType:
    | 'style1'
    | 'style2'
    | 'style3'
    | 'style-expand'
    | 'singleOption'
    | 'collapsed';
  isMobile?: boolean;
};

const FilterOptionByType = ({ option, currentModifierType, isMobile }: FilterOptionByTypeProps) => {
  const filterSettings = useFilterSettings();

  const {
    filterLayout,
    filterTreeHorizontalStyle,
    formatPipsRange,
    removePriceDecimal,
    precisionFormatLabelSlider,
    swatch_settings,
  } = filterSettings;

  const {
    generalSettings: { filterOptionBoxCharWidth, rangeSlidersSingleHandle },
    fileBaseUrl,
  } = useGeneralSettings();

  const {
    filterParams,
    filterOptionSelected,
    addFilterParams,
    handleChangeFilterParams,
    removeFilterParamsOption,
    setFilterOptionSelected,
  } = useFilterState();

  // if is mobile then we always run filter tree vertical
  const isFilterTreeVertical = filterLayout === 'vertical' || isMobile;

  const isHorizontalStyleExpand =
    !isFilterTreeVertical && filterTreeHorizontalStyle === 'style-expand';

  const { refineByHorizontal, setRefineByHorizontal } = useFilterTreeState();

  const { unitCurrency, rateCurrency } = useCurrency();

  const onToggle = () => {
    if (isFilterTreeVertical) {
      setFilterOptionSelected(option);
    } else {
      if (option?.filterOptionId === filterOptionSelected?.filterOptionId) {
        // set {} to close
        setFilterOptionSelected(null);
      } else {
        setFilterOptionSelected(option);
      }
    }
  };

  // Only apply for filter horizontal
  const onApply = () => {
    if (isFilterTreeVertical) return;

    filterParams && handleChangeFilterParams(filterParams, { requestInstantlyHorizontal: true });

    setRefineByHorizontal && setRefineByHorizontal(filterParams || {});
    // trigger on click Option Horizontal to close option when apply
    onToggle();
  };

  const onClear = () => {
    removeFilterParamsOption(option.filterOptionId, '');
  };

  const itemWrapperVerticalHidden = () => {
    // cover case use custom filter tree for collection with multi collection Id
    if (option.filterType === 'collection' && filterOptionSelected?.filterType === 'collection')
      return false;

    // other case
    return currentModifierType === 'singleOption'
      ? option.filterOptionId !== filterOptionSelected?.filterOptionId
      : false;
  };

  const itemWrapperModifiers = isFilterTreeVertical
    ? {
        hidden: itemWrapperVerticalHidden(),
        'full-height':
          currentModifierType === 'singleOption' && filterOptionSelected ? true : false,
      }
    : {
        hidden: isHorizontalStyleExpand
          ? false
          : !(
              filterOptionSelected?.filterOptionId &&
              filterOptionSelected?.filterOptionId === option.filterOptionId
            ),
        horizontal: true,
        [currentModifierType]: true,
      };

  const labelModifiers = {
    hidden: isFilterTreeVertical
      ? currentModifierType === 'singleOption' && filterOptionSelected
        ? true
        : false
      : false,
  };

  const titleModifiers = isFilterTreeVertical
    ? undefined
    : {
        opening: !!(
          filterOptionSelected?.filterOptionId &&
          filterOptionSelected?.filterOptionId === option.filterOptionId
        ),
      };

  const props = {
    isFilterTreeVertical,
    itemWrapperModifiers,
    labelModifiers,
    titleModifiers,
    currentModifierType,
    filterParams,
    filterSettings,
    refineByHorizontal,
    unitCurrency,
    onToggle,
    onClear,
    onApply,
  };

  const filterOptionItemSettings = pick(
    option,
    'prefix',
    'displayAllValuesInUppercaseForm',
    'selectType',
    'filterType',
    'swatchStyle'
  );

  return (
    <>
      {(() => {
        switch (option.displayType) {
          case FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_COLLECTIONS: {
            return (
              <FilterOptionMultiLevelCollections
                filterOptionProps={props}
                key={option.filterOptionId}
                option={option}
                useFilterOptionCollectionItem={useFilterOptionCollectionItem}
                filterOptionItemProps={({ handle, tags, key, displayName }) => ({
                  collectionId: key,
                  handle,
                  displayName,
                  tags,
                  settings: filterOptionItemSettings,
                })}
              />
            );
          }

          case FILTER_OPTION_DISPLAY_TYPE.LIST: {
            const { filterType } = option;

            if (filterType === FILTER_OPTION_TYPE.COLLECTION) {
              return (
                <FilterOptionMultiLevelCollections
                  filterOptionProps={props}
                  key={option.filterOptionId}
                  option={option}
                  useFilterOptionCollectionItem={useFilterOptionCollectionItem}
                  filterOptionItemProps={({ handle, key, label }) => ({
                    collectionId: key,
                    handle,
                    displayName: label,
                    tags: [],
                    settings: filterOptionItemSettings,
                  })}
                />
              );
            }

            if (filterType === FILTER_OPTION_TYPE.MULTI_LEVEL_TAG) {
              return (
                <FilterOptionList
                  filterOptionProps={props}
                  key={option.filterOptionId}
                  option={option}
                  filterOptionItemProps={({ tag, subTags, displayName, doc_count }) => ({
                    value: tag,
                    items: subTags,
                    docCount: doc_count,
                    settings: filterOptionItemSettings,
                    label: displayName,
                  })}
                />
              );
            }

            if (filterType === FILTER_OPTION_TYPE.PERCENT_SALE) {
              return (
                <FilterOptionPercentSale
                  filterOptionProps={props}
                  key={option.filterOptionId}
                  option={option}
                />
              );
            }

            if (
              filterType === FILTER_OPTION_TYPE.PRICE ||
              filterType === FILTER_OPTION_TYPE.VARIANTS_PRICE
            ) {
              return (
                <FilterOptionPriceList
                  filterOptionProps={props}
                  key={option.filterOptionId}
                  option={option}
                />
              );
            }

            if (filterType === FILTER_OPTION_TYPE.STOCK) {
              filterOptionItemSettings.selectType = 'multiple';
            }

            return (
              <FilterOptionList
                filterOptionProps={props}
                key={option.filterOptionId}
                option={option as FilterOptionListType}
                filterOptionItemProps={({ key, label, doc_count }) => ({
                  value: key,
                  label,
                  docCount: doc_count,
                  settings: filterOptionItemSettings,
                })}
              />
            );
          }

          case FILTER_OPTION_DISPLAY_TYPE.SWATCH: {
            const { values } = option;
            if (!values) return null;

            return (
              <FilterOptionSwatch
                filterOptionProps={props}
                key={option.filterOptionId}
                option={option}
                fileBaseUrl={fileBaseUrl}
                swatchSettings={swatch_settings}
                filterOptionItemProps={(optionItem) => ({
                  value: optionItem.key,
                  itemSettings: filterOptionItemSettings,
                })}
              />
            );
          }

          case FILTER_OPTION_DISPLAY_TYPE.BOX: {
            return (
              <FilterOptionBox
                filterOptionProps={props}
                key={option.filterOptionId}
                option={option}
                filterOptionItemProps={({ key, doc_count }) => ({
                  value: key,
                  docCount: doc_count,
                  settings: filterOptionItemSettings,
                })}
                settings={{
                  filterOptionBoxCharWidth: filterOptionBoxCharWidth || 14,
                  filterLayout,
                  filterTreeHorizontalStyle,
                }}
              />
            );
          }

          case FILTER_OPTION_DISPLAY_TYPE.RANGE: {
            if (!option.values) return null;
            if (option.values.min === option.values.max) return null;

            return (
              <FilterOptionRangeSlider
                filterOptionProps={props}
                key={option.filterOptionId}
                sliderStep={option.sliderStep}
                sliderRange={option.sliderRange}
                option={option}
                values={{ min: option.values.min, max: option.values.max }}
                optionIsFiltering={filterParams[option.filterOptionId]}
                settings={{
                  rangeSlidersSingleHandle,
                  formatPipsRange,
                  removePriceDecimal,
                  precisionFormatLabelSlider,
                  unitCurrency,
                  rateCurrency,
                }}
                onSelect={(value) => addFilterParams(option.filterOptionId, value, { force: true })}
              />
            );
          }

          case FILTER_OPTION_DISPLAY_TYPE.RATING: {
            return (
              <FilterOptionRating
                filterOptionProps={props}
                key={option.filterOptionId}
                option={option}
                starColor={option.starColor}
                showExactRating={option.showExactRating}
                filterOptionItemProps={({ key, from, to, doc_count }) => ({
                  value: key,
                  from,
                  to,
                  docCount: doc_count,
                  settings: filterOptionItemSettings,
                })}
              />
            );
          }

          default:
            break;
        }
      })()}
    </>
  );
};

export default FilterOptionByType;
